import React from 'react';
import { useTranslation } from 'react-i18next';

import {
  PlusOutlined,
  SearchOutlined,
  QuestionOutlined,
} from '@ant-design/icons';
import IconLibrary from '@components/IconLibrary/IconLibrary';
import { Button } from 'antd';
import { ButtonType, ButtonShape } from 'antd/lib/button';

import './CollapsibleButton.scss';

type StylePresets = 'search' | 'create' | 'export';

interface CollapsibleButtonType {
  type?: ButtonType | undefined; // extends all the types from antd Button
  preset?: StylePresets | undefined;
  children?: React.ReactNode | undefined;
  icon?: React.ReactNode | undefined;
  onClick: (e: React.MouseEvent<HTMLElement>) => void;
  className?: string | undefined;
  loading?: boolean | undefined;
  shape?: ButtonShape | undefined;
  disabled?: boolean | undefined;
}

function calculateTransitionDuration(
  presetConfig: { text: string } | null,
  children: React.ReactNode | undefined,
) {
  const durationPerChar = 150;

  let textLength = presetConfig?.text.length || 0;

  if (typeof children === 'string') {
    textLength = children.length;
  } else if (typeof children === 'object') {
    textLength = 2;
  }

  const duration = durationPerChar * textLength;
  return {
    transition: `max-width ${duration}ms`,
  };
}

function CollapsibleButton({
  onClick,
  children = undefined,
  preset = undefined,
  type = undefined,
  icon = undefined,
  className = undefined,
  loading = undefined,
  shape = undefined,
  disabled = false,
}: CollapsibleButtonType) {
  if (!children && !icon && !preset) {
    throw new Error(
      '可收合按鈕（CollapsibleButton）必須要傳入按鈕文案，或是使用 `preset` 預設定義類型',
    );
  }

  const { t } = useTranslation();

  function getStylePresets(presetValue: StylePresets) {
    switch (presetValue) {
      case 'search':
        return {
          type: 'primary' as ButtonType,
          icon: <SearchOutlined />,
          text: t('Common.Search'),
        };
      case 'create':
        return {
          type: 'default' as ButtonType,
          icon: <PlusOutlined />,
          text: t('Common.Add'),
        };
      case 'export':
        return {
          type: 'default' as ButtonType,
          icon: <IconLibrary name="ExportTR" />,
          text: t('Common.Download'),
        };
      default:
        throw new Error('可收合按鈕（CollapsibleButton）不支援的 `preset`');
    }
  }

  const presetConfig = preset ? getStylePresets(preset) : null;

  const transitionConfig = calculateTransitionDuration(presetConfig, children);

  return (
    <Button
      loading={loading}
      className={`CollapsibleButton ${className || ''}`}
      icon={icon || presetConfig?.icon || <QuestionOutlined />}
      onClick={onClick}
      type={type || presetConfig?.type || 'default'}
      shape={shape || 'default'}
      disabled={disabled}
    >
      <div className="CollapsibleButton__text" style={transitionConfig}>
        {children || presetConfig?.text}
      </div>
    </Button>
  );
}

export default CollapsibleButton;
